import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  isDevMode,
  OnInit,
  Optional,
  Output,
} from '@angular/core';

import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import {
  outputFormulaValueCoversStills,
  outputFormulaValueCoversVideos,
  outputFormulaValueIsEvFamily,
  outputFormulaValueIsRangeFamily,
  outputFormulaValueKindLabel,
  outputFormulaValueSpecIds,
} from 'src/app/models/output-formulas/output-formula-value';
import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';
import { FileSpecificationService } from 'src/app/services/file-specifications/file-specification.service';
import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import {
  ConventionSummary,
  evConventionSummaries,
  rangeConventionSummary,
  specLineFor,
} from './spec-label-helpers';
import {
  buildInterlockMap,
  EvInterlockBlocker,
  interlockTooltip,
} from './ev-interlock';

export type PickerMode = 'range' | 'ev';

export type PickedMedia = 'stills' | 'videos';

export interface PickedFormulaMedia {
  formulaId: string;
  media: PickedMedia;
}

export interface MediaCoverage {
  coversStills: boolean;
  coversVideos: boolean;
}

export interface OutputFormulaSelectionChange {
  mode: PickerMode;
  perMediaSelection: readonly PickedFormulaMedia[];
  formulas: OutputFormula[];
  coverage: MediaCoverage;
  coverageSummary: string;
  isValid: boolean;
  // Name lookups sourced from FileSpecificationService / FilenameConventionService
  // — exposed so consumers building OutputFormulaReference snapshots (the BE
  // gen-outputs payload) can populate the `name` fields the BE requires.
  specNamesById: ReadonlyMap<string, string>;
  conventionNamesById: ReadonlyMap<string, string>;
}

export interface MediaCellView {
  specLine: string;
  rangeConvention: ConventionSummary | null;
  evConventions: ConventionSummary[];
  sharesStillsConvention: boolean;
}

export interface FormulaRow {
  formula: OutputFormula;
  kindLabel: string;
  kindBadgeClass: 'range' | 'ev';
  coversStills: boolean;
  coversVideos: boolean;
  stillsCell: MediaCellView | null;
  videosCell: MediaCellView | null;
  isDisabled: boolean;
  blockerTooltip: string | null;
}

export const aggregateMediaCoverage = (
  formulas: readonly OutputFormula[],
): MediaCoverage => ({
  coversStills: formulas.some((formula) => outputFormulaValueCoversStills(formula.value)),
  coversVideos: formulas.some((formula) => outputFormulaValueCoversVideos(formula.value)),
});

export const describeMediaCoverage = (coverage: MediaCoverage): string => {
  if (!coverage.coversStills && !coverage.coversVideos) {
    return 'Coverage: no formulas picked';
  }
  if (coverage.coversStills && coverage.coversVideos) {
    return 'Coverage: Stills + Videos';
  }
  if (coverage.coversStills) {
    return 'Coverage: Stills only — no video formula picked';
  }
  return 'Coverage: Videos only — no stills formula picked';
};

export const isCoverageValid = (coverage: MediaCoverage): boolean =>
  coverage.coversStills || coverage.coversVideos;

@Component({
  selector: 'app-output-formula-picker',
  templateUrl: './output-formula-picker.component.html',
  styleUrls: ['./output-formula-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class OutputFormulaPickerComponent implements OnInit {
  @Input() organizationId = '';
  @Input() label = 'Output formulas';
  // "Review Filenames ›" button inline in the picker header. Parent gates via
  // reviewFilenamesDisabled (typically: no products picked, no formulas picked,
  // or a check request in-flight) and handles the request by calling its own
  // preview-filenames flow.
  @Input() reviewFilenamesDisabled = false;
  @Input() reviewFilesDisabled = false;
  @Input() showReviewFiles = false;
  @Output() readonly selectionChange = new EventEmitter<OutputFormulaSelectionChange>();
  @Output() readonly reviewFilenamesRequested = new EventEmitter<void>();
  @Output() readonly reviewFilesRequested = new EventEmitter<void>();

  availableFormulas: OutputFormula[] = [];
  fileSpecsById: Map<string, FileSpecification> = new Map();
  conventionsById: Map<string, FilenameConvention> = new Map();
  selectedFormulaIds: string[] = [];
  private bothPickedMedia: Map<string, Set<PickedMedia>> = new Map();

  private currentMode: PickerMode = 'range';

  @Input()
  set mode(next: PickerMode) {
    if (this.currentMode === next) {
      return;
    }
    this.currentMode = next;
    this.selectedFormulaIds = [];
    this.bothPickedMedia = new Map();
    this.emitSelectionChange();
  }
  get mode(): PickerMode {
    return this.currentMode;
  }

  constructor(
    private readonly outputFormulaService: OutputFormulaService,
    private readonly fileSpecificationService: FileSpecificationService,
    @Optional() private readonly filenameConventionService?: FilenameConventionService,
  ) {}

  ngOnInit(): void {
    this.loadFormulas();
    this.loadFileSpecs();
    this.loadConventions();
  }

  private loadFormulas(): void {
    this.outputFormulaService.list(this.organizationId).subscribe({
      next: (formulas) => {
        this.availableFormulas = formulas;
      },
      error: () => {
        this.availableFormulas = [];
      },
    });
  }

  private loadFileSpecs(): void {
    this.fileSpecificationService.list(this.organizationId).subscribe({
      next: (specs) => {
        this.fileSpecsById = new Map(specs.map((spec) => [spec.id, spec]));
      },
      error: () => {
        this.fileSpecsById = new Map();
      },
    });
  }

  private loadConventions(): void {
    if (this.filenameConventionService == null || this.organizationId === '') return;
    this.filenameConventionService.list(this.organizationId).subscribe({
      next: (conventions) => {
        this.conventionsById = new Map(conventions.map((convention) => [convention.id, convention]));
      },
      error: () => {
        this.conventionsById = new Map();
      },
    });
  }

  get hasAnyFormula(): boolean {
    return this.availableFormulas.length > 0;
  }

  get visibleFormulas(): OutputFormula[] {
    const inFamily =
      this.currentMode === 'range' ? outputFormulaValueIsRangeFamily : outputFormulaValueIsEvFamily;
    return this.availableFormulas.filter((formula) => inFamily(formula.value));
  }

  get selectedFormulas(): OutputFormula[] {
    return this.availableFormulas.filter((formula) =>
      this.selectedFormulaIds.includes(formula.id),
    );
  }

  private get conventionNamesById(): Map<string, string> {
    return new Map(
      [...this.conventionsById.entries()].map(([id, convention]) => [id, convention.name]),
    );
  }

  private get specNamesById(): Map<string, string> {
    return new Map(
      [...this.fileSpecsById.entries()].map(([id, spec]) => [id, spec.name]),
    );
  }

  private get interlockMap(): Map<string, EvInterlockBlocker> {
    if (this.currentMode !== 'ev') return new Map();
    return buildInterlockMap(this.visibleFormulas, this.selectedFormulas, this.conventionNamesById);
  }

  get rows(): FormulaRow[] {
    const interlock = this.interlockMap;
    return this.visibleFormulas.map((formula) => this.buildRow(formula, interlock));
  }

  private buildRow(
    formula: OutputFormula,
    interlock: ReadonlyMap<string, EvInterlockBlocker>,
  ): FormulaRow {
    const coversStills = outputFormulaValueCoversStills(formula.value);
    const coversVideos = outputFormulaValueCoversVideos(formula.value);
    const blocker = interlock.get(formula.id) ?? null;
    return {
      formula,
      kindLabel: outputFormulaValueKindLabel(formula.value.kind),
      kindBadgeClass: outputFormulaValueIsEvFamily(formula.value) ? 'ev' : 'range',
      coversStills,
      coversVideos,
      stillsCell: coversStills ? this.buildStillsCell(formula) : null,
      videosCell: coversVideos ? this.buildVideosCell(formula) : null,
      isDisabled: blocker != null,
      blockerTooltip: blocker != null ? interlockTooltip(blocker) : null,
    };
  }

  private buildStillsCell(formula: OutputFormula): MediaCellView {
    const value = formula.value;
    switch (value.kind) {
      case 'rangeStills':
      case 'rangeBoth':
        return {
          specLine: specLineFor(this.fileSpecsById.get(value.stills.specId)),
          rangeConvention: rangeConventionSummary(this.conventionsById.get(value.stills.conventionId)),
          evConventions: [],
          sharesStillsConvention: false,
        };
      case 'evStills':
      case 'evBoth':
        return {
          specLine: specLineFor(this.fileSpecsById.get(value.stillsSpecId)),
          rangeConvention: null,
          evConventions: evConventionSummaries(value.evConventionIds, this.conventionsById),
          sharesStillsConvention: false,
        };
      default:
        return { specLine: 'Unknown spec', rangeConvention: null, evConventions: [], sharesStillsConvention: false };
    }
  }

  private buildVideosCell(formula: OutputFormula): MediaCellView {
    const value = formula.value;
    switch (value.kind) {
      case 'rangeVideos':
      case 'rangeBoth':
        return {
          specLine: specLineFor(this.fileSpecsById.get(value.videos.specId)),
          rangeConvention: rangeConventionSummary(this.conventionsById.get(value.videos.conventionId)),
          evConventions: [],
          sharesStillsConvention:
            value.kind === 'rangeBoth' && value.videos.conventionId === value.stills.conventionId,
        };
      case 'evVideos':
      case 'evBoth':
        return {
          specLine: specLineFor(this.fileSpecsById.get(value.videosSpecId)),
          rangeConvention: null,
          evConventions: evConventionSummaries(value.evConventionIds, this.conventionsById),
          sharesStillsConvention: false,
        };
      default:
        return { specLine: 'Unknown spec', rangeConvention: null, evConventions: [], sharesStillsConvention: false };
    }
  }

  onRowToggle(formulaId: string, checked: boolean): void {
    const next = checked
      ? [...this.selectedFormulaIds, formulaId]
      : this.selectedFormulaIds.filter((id) => id !== formulaId);
    this.onSelectionChanged(next);
  }

  onRowFormulaToggle(formula: OutputFormula, checked: boolean): void {
    if (!this.isBoth(formula)) {
      this.onRowToggle(formula.id, checked);
      return;
    }
    const nextPickedMedia = new Map(this.bothPickedMedia);
    if (checked) {
      nextPickedMedia.set(formula.id, new Set<PickedMedia>(['stills', 'videos']));
    } else {
      nextPickedMedia.delete(formula.id);
    }
    this.bothPickedMedia = nextPickedMedia;
    const isSelected = this.selectedFormulaIds.includes(formula.id);
    if (checked && !isSelected) {
      this.selectedFormulaIds = [...this.selectedFormulaIds, formula.id];
    } else if (!checked && isSelected) {
      this.selectedFormulaIds = this.selectedFormulaIds.filter((id) => id !== formula.id);
    }
    this.emitSelectionChange();
  }

  isRowFullyPicked(formula: OutputFormula): boolean {
    if (!this.isBoth(formula)) return this.isRowSelected(formula.id);
    return (this.bothPickedMedia.get(formula.id)?.size ?? 0) === 2;
  }

  isRowPartiallyPicked(formula: OutputFormula): boolean {
    if (!this.isBoth(formula)) return false;
    const size = this.bothPickedMedia.get(formula.id)?.size ?? 0;
    return size > 0 && size < 2;
  }

  onMediaToggle(formulaId: string, media: PickedMedia, checked: boolean): void {
    const formula = this.availableFormulas.find((candidate) => candidate.id === formulaId);
    if (formula != null && !this.isBoth(formula)) {
      if (!this.formulaCoversMedia(formula, media)) return;
      this.onRowToggle(formulaId, checked);
      return;
    }
    const current = new Set(this.bothPickedMedia.get(formulaId) ?? []);
    if (checked) {
      current.add(media);
    } else {
      current.delete(media);
    }
    const nextPickedMedia = new Map(this.bothPickedMedia);
    if (current.size === 0) {
      nextPickedMedia.delete(formulaId);
    } else {
      nextPickedMedia.set(formulaId, current);
    }
    this.bothPickedMedia = nextPickedMedia;

    const isSelected = this.selectedFormulaIds.includes(formulaId);
    if (current.size > 0 && !isSelected) {
      this.selectedFormulaIds = [...this.selectedFormulaIds, formulaId];
    } else if (current.size === 0 && isSelected) {
      this.selectedFormulaIds = this.selectedFormulaIds.filter((id) => id !== formulaId);
    }
    this.emitSelectionChange();
  }

  private formulaCoversMedia(formula: OutputFormula, media: PickedMedia): boolean {
    return media === 'stills'
      ? outputFormulaValueCoversStills(formula.value)
      : outputFormulaValueCoversVideos(formula.value);
  }

  isRangeBoth(formula: OutputFormula): boolean {
    return formula.value.kind === 'rangeBoth';
  }

  isBoth(formula: OutputFormula): boolean {
    const kind = formula.value.kind;
    return kind === 'rangeBoth' || kind === 'evBoth';
  }

  isMediaPicked(formulaId: string, media: PickedMedia): boolean {
    const formula = this.availableFormulas.find((candidate) => candidate.id === formulaId);
    if (formula != null && !this.isBoth(formula)) {
      return this.formulaCoversMedia(formula, media) && this.isRowSelected(formulaId);
    }
    return this.bothPickedMedia.get(formulaId)?.has(media) ?? false;
  }

  onSelectionChanged(formulaIds: string[]): void {
    this.selectedFormulaIds = [...new Set(formulaIds)];
    const stillSelected = new Set(this.selectedFormulaIds);
    this.bothPickedMedia = new Map(
      [...this.bothPickedMedia.entries()].filter(([id]) => stillSelected.has(id)),
    );
    this.emitSelectionChange();
  }

  onSelectAllToggle(checked: boolean): void {
    if (checked) {
      const enabledRows = this.rows.filter((row) => !row.isDisabled);
      const enabledIds = enabledRows.map((row) => row.formula.id);
      const seededPickedMedia = new Map<string, Set<PickedMedia>>();
      enabledRows.forEach((row) => {
        if (this.isBoth(row.formula)) {
          seededPickedMedia.set(row.formula.id, new Set<PickedMedia>(['stills', 'videos']));
        }
      });
      this.bothPickedMedia = seededPickedMedia;
      this.onSelectionChanged(enabledIds);
      return;
    }
    this.onSelectionChanged([]);
  }

  get selectAllTotalCount(): number {
    return this.visibleFormulas.length;
  }

  get selectedVisibleCount(): number {
    const visibleIds = new Set(this.visibleFormulas.map((formula) => formula.id));
    return this.selectedFormulaIds.filter((id) => visibleIds.has(id)).length;
  }

  isRowSelected(formulaId: string): boolean {
    return this.selectedFormulaIds.includes(formulaId);
  }

  isRowDisabled(formulaId: string): boolean {
    return this.interlockMap.has(formulaId);
  }

  blockerTooltipFor(formulaId: string): string | null {
    const blocker = this.interlockMap.get(formulaId);
    return blocker != null ? interlockTooltip(blocker) : null;
  }

  variantKindLabel(formula: OutputFormula): string {
    return outputFormulaValueKindLabel(formula.value.kind);
  }

  specNamesForFormula(formulaId: string): string[] {
    const formula = this.availableFormulas.find((candidate) => candidate.id === formulaId);
    if (formula == null) return [];
    return outputFormulaValueSpecIds(formula.value).map(
      (id) => this.fileSpecsById.get(id)?.name ?? 'Unknown spec',
    );
  }

  private mediaEntriesFor(formula: OutputFormula): PickedFormulaMedia[] {
    const value = formula.value;
    if (value.kind === 'rangeStills') return [{ formulaId: formula.id, media: 'stills' }];
    if (value.kind === 'rangeVideos') return [{ formulaId: formula.id, media: 'videos' }];
    if (value.kind === 'evStills') return [{ formulaId: formula.id, media: 'stills' }];
    if (value.kind === 'evVideos') return [{ formulaId: formula.id, media: 'videos' }];
    const picked = this.bothPickedMedia.get(formula.id) ?? new Set<PickedMedia>(['stills', 'videos']);
    return [...picked].map((media) => ({ formulaId: formula.id, media }));
  }

  private buildPerMediaSelection(): PickedFormulaMedia[] {
    return this.selectedFormulas.flatMap((formula) => this.mediaEntriesFor(formula));
  }

  private emitSelectionChange(): void {
    const perMediaSelection = this.buildPerMediaSelection();
    const formulaIds = Array.from(new Set(perMediaSelection.map((entry) => entry.formulaId)));
    const formulas = this.availableFormulas.filter((formula) => formulaIds.includes(formula.id));
    const coverage = aggregateMediaCoverage(formulas);
    if (isDevMode()) {
      console.log('[picker] emit', {
        formulaCount: formulas.length,
        formulasHaveValue: formulas.map((f) => ({ id: f.id, hasValue: !!f.value, kind: f.value?.kind })),
        specNamesByIdSize: this.specNamesById.size,
        conventionNamesByIdSize: this.conventionNamesById.size,
      });
    }
    this.selectionChange.emit({
      mode: this.currentMode,
      perMediaSelection,
      formulas,
      coverage,
      coverageSummary: describeMediaCoverage(coverage),
      isValid: isCoverageValid(coverage),
      specNamesById: this.specNamesById,
      conventionNamesById: this.conventionNamesById,
    });
  }
}
